import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { Toaster as Sonner, type ToasterProps } from "sonner";
import { useTheme } from "@/hooks/use-theme";

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useTheme();

  return (
    // Sonner pointer-captures on pointerdown, which confines text selection and starts swipe.
    // Block it for presses on toast text so text is selectable; other areas still swipe-to-dismiss.
    <div
      onPointerDownCapture={(event) => {
        const target = event.target as HTMLElement;
        if (!target.closest("[data-title], [data-description]")) {
          return;
        }
        event.stopPropagation();
        // stopPropagation also suppresses Sonner's pointerdown -> setInteracting (auto-dismiss pause);
        // re-arm it synthetically, and clear on window pointerup — a selection drag can end outside the ol.
        const toaster = target.closest("[data-sonner-toaster]");
        if (toaster) {
          toaster.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true }));
          window.addEventListener(
            "pointerup",
            () => toaster.dispatchEvent(new PointerEvent("pointerup", { bubbles: true })),
            { once: true },
          );
        }
      }}
    >
      <Sonner
        theme={theme as ToasterProps["theme"]}
        className="toaster group"
        icons={{
          success: <CircleCheckIcon className="size-4" />,
          info: <InfoIcon className="size-4" />,
          warning: <TriangleAlertIcon className="size-4" />,
          error: <OctagonXIcon className="size-4" />,
          loading: <Loader2Icon className="size-4 animate-spin" />,
        }}
        style={
          {
            "--normal-bg": "var(--popover)",
            "--normal-text": "var(--popover-foreground)",
            "--normal-border": "var(--border)",
            "--border-radius": "var(--radius)",
          } as React.CSSProperties
        }
        toastOptions={{
          classNames: {
            toast: "cn-toast",
          },
        }}
        {...props}
      />
    </div>
  );
};

export { Toaster };
